"use client"

import { useState, useEffect } from "react"
import { Backdrop, CircularProgress } from "@mui/material"
import LoginHeader from "./components/LoginHeader.js"
import SideNavigation from "./components/SideNavigation.js"
import Breadcrumbs from "@mui/material/Breadcrumbs"
import Typography from "@mui/material/Typography"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import AADHeader from "./components/AADHeader.js"
import { useNavigate, useLocation } from "react-router-dom"
import { Link } from "react-router-dom"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { InputText } from "primereact/inputtext"
import { FilterMatchMode } from "primereact/api"
import { Tooltip } from "@mui/material"
import "./table.css"
import { format } from "date-fns"
import Swal from "sweetalert2"
import { BlobServiceClient } from "@azure/storage-blob"
import usePermissions from "./hooks/usePermissions";
import { PERMISSIONS } from "./constants/permissions";

// Custom styles for filter form with brand color (#407775)
const filterFormStyles = `
  /* Filter form buttons - Apply button (Primary) */
  .p-column-filter-overlay-menu .p-column-filter-buttonbar .p-button.p-button-primary,
  .p-column-filter-overlay-menu .p-column-filter-buttonbar .p-button:not(.p-button-secondary) {
    background: #407775 !important;
    border-color: #407775 !important;
    color: #ffffff !important;
  }
  .p-column-filter-overlay-menu .p-column-filter-buttonbar .p-button.p-button-primary:hover,
  .p-column-filter-overlay-menu .p-column-filter-buttonbar .p-button:not(.p-button-secondary):hover {
    background: #35615f !important;
    border-color: #35615f !important;
    color: #ffffff !important;
  }
  .p-column-filter-overlay-menu .p-column-filter-buttonbar .p-button.p-button-primary:focus,
  .p-column-filter-overlay-menu .p-column-filter-buttonbar .p-button:not(.p-button-secondary):focus {
    box-shadow: 0 0 0 0.2rem rgba(64, 119, 117, 0.2) !important;
  }
  
  /* Filter form buttons - Clear button (Secondary/Outlined) */
  .p-column-filter-overlay-menu .p-column-filter-buttonbar .p-button.p-button-secondary,
  .p-column-filter-overlay-menu .p-column-filter-buttonbar .p-button.p-button-outlined {
    background: transparent !important;
    border-color: #407775 !important;
    color: #407775 !important;
  }
  .p-column-filter-overlay-menu .p-column-filter-buttonbar .p-button.p-button-secondary:hover,
  .p-column-filter-overlay-menu .p-column-filter-buttonbar .p-button.p-button-outlined:hover {
    background: rgba(64, 119, 117, 0.1) !important;
    border-color: #407775 !important;
    color: #407775 !important;
  }
  .p-column-filter-overlay-menu .p-column-filter-buttonbar .p-button.p-button-secondary:focus,
  .p-column-filter-overlay-menu .p-column-filter-buttonbar .p-button.p-button-outlined:focus {
    box-shadow: 0 0 0 0.2rem rgba(64, 119, 117, 0.2) !important;
  }
  
  /* Add Rule link - all variations */
  .p-column-filter-overlay-menu .p-column-filter-add-rule,
  .p-column-filter-overlay-menu .p-column-filter-add-rule a,
  .p-column-filter-overlay-menu .p-column-filter-add-rule button,
  .p-column-filter-overlay-menu .p-column-filter-add-rule span,
  .p-column-filter-overlay-menu .p-column-filter-add-rule .p-link {
    color: #407775 !important;
  }
  .p-column-filter-overlay-menu .p-column-filter-add-rule:hover,
  .p-column-filter-overlay-menu .p-column-filter-add-rule a:hover,
  .p-column-filter-overlay-menu .p-column-filter-add-rule button:hover,
  .p-column-filter-overlay-menu .p-column-filter-add-rule span:hover,
  .p-column-filter-overlay-menu .p-column-filter-add-rule .p-link:hover {
    color: #35615f !important;
    text-decoration: underline;
  }
  
  /* Add Rule button/link styling */
  .p-column-filter-overlay-menu .p-column-filter-add-rule .p-button {
    color: #407775 !important;
    background: transparent !important;
    border-color: transparent !important;
  }
  .p-column-filter-overlay-menu .p-column-filter-add-rule .p-button:hover {
    color: #35615f !important;
    background: rgba(64, 119, 117, 0.1) !important;
  }
  
  /* Filter input focus border */
  .p-column-filter-overlay-menu .p-inputtext:focus,
  .p-column-filter-overlay-menu .p-dropdown:not(.p-disabled).p-focus {
    border-color: #407775 !important;
    box-shadow: 0 0 0 0.2rem rgba(64, 119, 117, 0.2) !important;
  }
  
  /* Filter dropdown active/highlight */
  .p-column-filter-overlay-menu .p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
    background: rgba(64, 119, 117, 0.1) !important;
    color: #407775 !important;
  }
  
  /* Remove Rule button text */
  .p-column-filter-overlay-menu .p-column-filter-remove-button,
  .p-column-filter-overlay-menu .p-column-filter-remove-button .p-button {
    color: #dc2626 !important;
  }
  
  /* Match mode dropdown and operator text */
  .p-column-filter-overlay-menu .p-column-filter-operator,
  .p-column-filter-overlay-menu .p-column-filter-matchmode-dropdown {
    color: #374151 !important;
  }
`

const EmployeeCommuteSurvey = () => {
  const navigate = useNavigate()
  const { has: hasPermission } = usePermissions();
  const canStartAssessment = hasPermission(PERMISSIONS.START_ASSESSMENTS);
  const location = useLocation()
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768)
  const companyid = localStorage.getItem("companyid")
  const [loading, setLoading] = useState(false)
  const [deepDiveLoading, setDeepDiveLoading] = useState(false)
  const [data, setData] = useState([]) // State to hold table data
  const [activeMainTab, setActiveMainTab] = useState("overview")
  const [activeSubTab, setActiveSubTab] = useState("employee-level")
  const [hoveredMainTab, setHoveredMainTab] = useState(null) // Added hover states for main tabs
  const [employeeEmissionsData, setEmployeeEmissionsData] = useState([])
  const [siteEmissionsData, setSiteEmissionsData] = useState([])
  const textFilter = () => ({ value: null, matchMode: FilterMatchMode.CONTAINS })

  const getInitialEmployeeEmissionsFilters = () => ({
    global: textFilter(),
    employeeID: textFilter(),
    name: textFilter(),
    siteLocation: textFilter(),
    reportingPeriod: textFilter(),
    officeDays: textFilter(),
    wfhDays: textFilter(),
    primaryModeOfTransport: textFilter(),
    fuelType: textFilter(),
    transportType: textFilter(),
    transportEmissionsPerWeek: textFilter(),
    wfhEmissions: textFilter(),
    wfhEnergyAccuracy: textFilter(),
    totalEmissionsPerWeek: textFilter(),
    totalEmissionsPerYear: textFilter(),
  })

  const getInitialSiteEmissionsFilters = () => ({
    global: textFilter(),
    reportingPeriod: textFilter(),
    siteName: textFilter(),
    totalEmployees: textFilter(),
    respondents: textFilter(),
    nonRespondents: textFilter(),
    transportEmissions: textFilter(),
    wfhEmissions: textFilter(),
    totalEmissions: textFilter(),
  })
  const [employeeEmissionsFilters, setEmployeeEmissionsFilters] = useState(() => getInitialEmployeeEmissionsFilters())
  const [employeeEmissionsGlobalFilterValue, setEmployeeEmissionsGlobalFilterValue] = useState("")
  const [siteEmissionsFilters, setSiteEmissionsFilters] = useState(() => getInitialSiteEmissionsFilters())
  const [siteEmissionsGlobalFilterValue, setSiteEmissionsGlobalFilterValue] = useState("")
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    timestampDisplay: { value: null, matchMode: FilterMatchMode.CONTAINS },
    surveyPeriodDisplay: { value: null, matchMode: FilterMatchMode.CONTAINS },
    expirationDateNew: { value: null, matchMode: FilterMatchMode.CONTAINS },
  })
  const [globalFilterValue, setGlobalFilterValue] = useState("")
  const username = localStorage.getItem("userName");
  
  // const employeeEmissionsData = [
  //   {
  //     legalEntity: "Legal entity 1",
  //     employeeId: "EMP001",
  //     name: "Alice Kumar",
  //     siteLocation: "Bangalore",
  //     reportingPeriod: "2022-2023",
  //     officeDays: 5,
  //     wfhDays: 0,
  //     primaryModeTransport: "Car",
  //     fuelType: "Petrol",
  //     transportType: "Petrol (Car)",
  //     transportEmissions: 14,
  //     wfhEmissions: 0,
  //     wfhEnergyAccuracy: "",
  //     totalEmissionsWeek: 14,
  //     totalEmissionsYear: 560,
  //   },
  //   {
  //     legalEntity: "Legal entity 1",
  //     employeeId: "EMP002",
  //     name: "Raj Mehta",
  //     siteLocation: "Bangalore",
  //     reportingPeriod: "2022-2023",
  //     officeDays: 3,
  //     wfhDays: 2,
  //     primaryModeTransport: "Train",
  //     fuelType: "Petrol",
  //     transportType: "National train",
  //     transportEmissions: 12,
  //     wfhEmissions: 2.3,
  //     wfhEnergyAccuracy: "Average data",
  //     totalEmissionsWeek: 3.6,
  //     totalEmissionsYear: 360,
  //   },
  //   {
  //     legalEntity: "Legal entity 1",
  //     employeeId: "EMP003",
  //     name: "Sarah D'Souza",
  //     siteLocation: "Bangalore",
  //     reportingPeriod: "2022-2023",
  //     officeDays: 2,
  //     wfhDays: 3,
  //     primaryModeTransport: "EV",
  //     fuelType: "Petrol",
  //     transportType: "Electric (EV)",
  //     transportEmissions: 2.2,
  //     wfhEmissions: 3.4,
  //     wfhEnergyAccuracy: "Usage per day",
  //     totalEmissionsWeek: 3.6,
  //     totalEmissionsYear: 360,
  //   },
  //   {
  //     legalEntity: "Legal entity 1",
  //     employeeId: "EMP004",
  //     name: "John Thomas",
  //     siteLocation: "Bangalore",
  //     reportingPeriod: "2022-2023",
  //     officeDays: 4,
  //     wfhDays: 1,
  //     primaryModeTransport: "Bike",
  //     fuelType: "Petrol",
  //     transportType: "Petrol (Bike)",
  //     transportEmissions: 1.6,
  //     wfhEmissions: 1.1,
  //     wfhEnergyAccuracy: "Usage per day",
  //     totalEmissionsWeek: 2.7,
  //     totalEmissionsYear: 360,
  //   },
  // ]

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev)
  }

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false)
      } else {
        setIsSidebarOpen(true)
      }
    }

    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  async function listSurvey() {
    try {
      
      const companyid = localStorage.getItem("companyid")
      var myHeaders = new Headers()
      var requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
      }
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}list-survey`, {
        method: "GET",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          companyId: companyid,
          username: username,
        },
      })
      if (response.status === 200) {
        const responseJson = await response.json()
        if (responseJson.success === false || responseJson.status === 400) {
          
        } else {
          // Sort data by survey creation date (timestamp) in descending order
          const sortedData = responseJson.data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
          // Add pre-formatted display fields (for filters) and numeric ms fields (for reliable sorting)
          const processedData = sortedData.map((item) => {
            const ts = item.timestamp ? new Date(item.timestamp) : null
            let fromD = item.surveyFromDate ? new Date(item.surveyFromDate) : null
            let toD = item.surveyToDate ? new Date(item.surveyToDate) : null
            if (fromD) fromD = new Date(fromD.toISOString().split("T")[0])
            if (toD) toD = new Date(toD.toISOString().split("T")[0])
            const expD = item.expirationDate ? new Date(item.expirationDate) : null
            return {
              ...item,
              timestampDisplay: ts && !isNaN(ts) ? format(ts, "dd MMM yyyy") : "Invalid date",
              surveyPeriodDisplay:
                fromD && !isNaN(fromD) && toD && !isNaN(toD)
                  ? `${format(fromD, "MMM-yyyy")} to ${format(toD, "MMM-yyyy")}`
                  : "Invalid date",
              timestampMs: ts && !isNaN(ts) ? ts.getTime() : 0,
              surveyFromDateMs: fromD && !isNaN(fromD) ? fromD.getTime() : 0,
              expirationDateMs: expD && !isNaN(expD) ? expD.getTime() : 0,
            }
          })
          setData(processedData)
          
        }
      } else {
        
      }
    } catch (error) {
      console.error(error)
      
    }
  }

  useEffect(() => {
    listSurvey()
    fetchEmployeeEmissionsData()
  }, [location.key])

  async function fetchEmployeeEmissionsData() {
    try {
      setDeepDiveLoading(true)
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}employee-commute-deep-dive-employee-level`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          companyid: companyid,
          username: username,
        },
      })
      if (response.status === 200) {
        const responseJson = await response.json()
        if (responseJson.success === false || responseJson.status === 400) {
          setDeepDiveLoading(false)
        } else {
          const rows = Array.isArray(responseJson.data) ? responseJson.data : []
          const flattenEmissions = (list, key) => {
            return list.flatMap((item) => {
              if (!item || typeof item !== "object") return []
              const chunk = item[key]
              if (Array.isArray(chunk)) return chunk
              if (chunk != null && typeof chunk === "object") return [chunk]
              return [item]
            })
          }
          const employeeData = flattenEmissions(rows, "employeeLevelEmissions")
          const siteData = flattenEmissions(rows, "siteLevelEmissions")
          setEmployeeEmissionsData(employeeData)
          setSiteEmissionsData(siteData)
          setEmployeeEmissionsFilters(getInitialEmployeeEmissionsFilters())
          setEmployeeEmissionsGlobalFilterValue("")
          setSiteEmissionsFilters(getInitialSiteEmissionsFilters())
          setSiteEmissionsGlobalFilterValue("")
          setDeepDiveLoading(false)
        }
      } else {
        setDeepDiveLoading(false)
      }
    } catch (error) {
      console.error(error)
      setDeepDiveLoading(false)
    }
  }

  // async function fetchSiteEmissionsData() {
  //   try {
  //     const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}employee-commute-deep-dive-site-level`, {
  //       method: "POST",
  //       headers: {
  //         "Content-type": "application/json; charset=UTF-8",
  //         companyid: companyid,
  //       },
  //     })
  //     if (response.status === 200) {
  //       const responseJson = await response.json()
  //       if (responseJson.success === false || responseJson.status === 400) {
  //         setLoading(false)
  //       } else {
  //         setSiteEmissionsData(responseJson.data)
  //         setLoading(false)
  //       }
  //     } else {
  //       setLoading(false)
  //     }
  //   } catch (error) {
  //     console.error(error)  
  //     setLoading(false)
  //   }
  // }

  const onGlobalFilterChange = (e) => {
    const value = e.target.value
    setFilters((prevFilters) => ({
      ...prevFilters,
      global: { value: value, matchMode: FilterMatchMode.CONTAINS },
    }))
    setGlobalFilterValue(value)
  }

  const onEmployeeEmissionsGlobalFilterChange = (e) => {
    const value = e.target.value
    setEmployeeEmissionsFilters((prev) => ({
      ...prev,
      global: { value, matchMode: FilterMatchMode.CONTAINS },
    }))
    setEmployeeEmissionsGlobalFilterValue(value)
  }

  const onSiteEmissionsGlobalFilterChange = (e) => {
    const value = e.target.value
    setSiteEmissionsFilters((prev) => ({
      ...prev,
      global: { value, matchMode: FilterMatchMode.CONTAINS },
    }))
    setSiteEmissionsGlobalFilterValue(value)
  }

  const employeeEmissionsGlobalFilterFields = ["employeeID", "name", "siteLocation", "reportingPeriod", "officeDays", "wfhDays", "primaryModeOfTransport", "fuelType", "transportType", "transportEmissionsPerWeek", "wfhEmissions", "wfhEnergyAccuracy", "totalEmissionsPerWeek", "totalEmissionsPerYear"]
  const siteEmissionsGlobalFilterFields = ["reportingPeriod", "siteName", "totalEmployees", "respondents", "nonRespondents", "transportEmissions", "wfhEmissions", "totalEmissions"]

  const renderHeader1 = () => {
    const loginType = localStorage.getItem("loginType")
    if (loginType === "AAD") {
      return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />
    }
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />
  }

  const renderHeader = () => {
    return (
      <div className="flex justify-content-end">
        <span className="p-input-icon-left">
          <img src="assets/img/search.png" className="search-i" alt="search" />
          <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Search" />
        </span>
      </div>
    )
  }

  const header = renderHeader()

  const serialNumberBodyTemplate = (rowData, { rowIndex }) => {
    return `${rowIndex + 1}.` // Adds a dot to the serial number
  }

  const formatSurveyPeriod = (rowData) => {
    let fromDate = new Date(rowData.surveyFromDate)
    let toDate = new Date(rowData.surveyToDate)
    if (isNaN(fromDate) || isNaN(toDate)) {
      console.error("Invalid date detected", {
        surveyFromDate: rowData.surveyFromDate,
        surveyToDate: rowData.surveyToDate,
      })
      return "Invalid date"
    }
    fromDate = new Date(fromDate.toISOString().split("T")[0]) // Strip the time part
    toDate = new Date(toDate.toISOString().split("T")[0]) // Strip the time part
    const formattedFromDate = format(fromDate, "MMM-yyyy")
    const formattedToDate = format(toDate, "MMM-yyyy")
    return `${formattedFromDate} to ${formattedToDate}`
  }

  const formatSurveyFromDate = (rowData) => {
    let formattedFromDate = new Date(rowData.surveyFromDate)
    formattedFromDate = new Date(formattedFromDate.toISOString().split("T")[0])
    if (isNaN(formattedFromDate.getTime())) {
      return "Invalid date"
    }
    return format(formattedFromDate, "dd MMM yyyy")
  }

  const formatExpiryDate = (rowData) => {
    let expiryDate = new Date(rowData.expirationDate)
    if (isNaN(expiryDate.getTime())) {
      return "Invalid date"
    }
    expiryDate = new Date(expiryDate.toISOString().split("T")[0])
    return format(expiryDate, "dd MMM yyyy")
  }

  const formatTimestamp = (rowData) => {
    const timestamp = new Date(rowData.timestamp)
    if (isNaN(timestamp.getTime())) {
      return "Invalid date"
    }
    return format(timestamp, "dd MMM yyyy")
  }

  const isSurveyExpired = (expirationDateValue) => {
    const expirationDate = new Date(expirationDateValue)
    if (isNaN(expirationDate.getTime())) {
      return true
    }
    const today = new Date()
    const expirationDateOnly = new Date(expirationDate.toISOString().split("T")[0])
    const todayOnly = new Date(today.toISOString().split("T")[0])
    return expirationDateOnly < todayOnly
  }

  const downloadResponse = async (rowData) => {
    console.log(rowData)
    try {
      setLoading(true)
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}GetSASToken`, {
        method: "GET",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
      })

      if (res.status === 200) {
        const responseJsonSas = await res.json()
        const connectionString = responseJsonSas.data
        const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}download-response`, {
          method: "GET",
          headers: {
            "Content-type": "application/json; charset=UTF-8",
            companyid: companyid,
            username: username,
            from: rowData.surveyFromDate,
            to: rowData.surveyToDate,
          },
        })
        if (response.status === 200) {
          const responseJson = await response.json()
          if (responseJson.success === false || responseJson.status === 400) {
            setLoading(false)
            Swal.fire({
              icon: "error",
              html: `<h4 style="color:#cc2229;font-size: 20px;">Failure!</h4><p style="font-size:16px;">${responseJson.message}</p>`,
              iconHtml: '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
            })
          } else {
            const blobServiceClient = new BlobServiceClient(`${connectionString}`)
            const containerClient = blobServiceClient.getContainerClient("ecoprismfilecontainer")
            const blobClient = containerClient.getBlobClient(
              `${responseJson.data.folderPath}${responseJson.data.fileName}`,
            )
            const downloadBlockBlobResponse = await blobClient.download(0)
            const blob = await downloadBlockBlobResponse.blobBody
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement("a")
            link.href = url
            link.setAttribute("download", responseJson.data.fileName)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            setLoading(false)
            Swal.fire({
              icon: "success",
              html: '<h4 style="color:#407775;font-size: 20px;">Success!</h4><p style="font-size:16px;">Responses downloaded successfully.</p>',
              iconHtml: '<img src="assets/img/successicon.png" style="width: 55px; height: 55px;" />',
            })
          }
        } else {
          setLoading(false)
          Swal.fire({
            icon: "error",
            html: `<h4 style="color:#cc2229;font-size: 20px;">Failure!</h4><p style="font-size:16px;">Unable to download responses. Please try again later.</p>`,
            iconHtml: '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
          })
        }
      } else {
        setLoading(false)
        Swal.fire({
          icon: "error",
          html: '<h4 style="color:#cc2229;font-size: 20px;">Failure!</h4><p style="font-size:16px;">Unable to retrieve SAS token. Please try again later.</p>',
          iconHtml: '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
        })
      }
    } catch (error) {
      console.error(error)
      setLoading(false)
      Swal.fire({
        icon: "error",
        html: '<h4 style="color:#cc2229;font-size: 20px;">Failure!</h4><p style="font-size:16px;">An unexpected error occurred. Please try again later.</p>',
        iconHtml: '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
      })
    }
  }

  const sendReminder = async (rowData) => {
    console.log(rowData)
    try {
      setLoading(true)
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}reminder-email`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        body: JSON.stringify({
          emailTo: rowData.emailTo,
          emailSubject: rowData.emailSubject,
          surveyLink: rowData.surveyLink,
          surveyFromDate: rowData.surveyFromDate,
          surveyToDate: rowData.surveyToDate,
          expirationDate: rowData.expirationDate,
        }),
      })
      if (response.status === 200) {
        const responseJson = await response.json()
        if (responseJson.success === false || responseJson.status === 400) {
          setLoading(false)
        } else {
          setLoading(false)
          Swal.fire({
            icon: "success",
            html: `<h4 style="color:#407775;font-size: 20px;">Success!</h4><p style="font-size:16px;">Reminder sent successfully</p>`,
            iconHtml: '<img src="assets/img/successicon.png" style=" height: 55px;" />',
          })
        }
      } else {
        setLoading(false)
      }
    } catch (error) {
      console.error(error)
      setLoading(false)
    }
  }

  const renderOverviewTable = () => {
    return (
      <DataTable
        value={data}
        paginator
        rows={10}
        rowsPerPageOptions={[10, 20, 25, 50]}
        tableStyle={{ minWidth: "50rem" }}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        filters={filters}
        filterDisplay="row"
        globalFilterFields={["timestampDisplay", "surveyPeriodDisplay", "expirationDateNew"]}
        header={header}
        emptyMessage="No data found."
      >
        <Column body={serialNumberBodyTemplate} style={{ width: "3rem", textAlign: "center", paddingLeft: "30px" }} />
        <Column
          header="Survey initiated on"
          field="timestampMs"
          filterField="timestampDisplay"
          body={formatTimestamp}
          sortable
          filter
          filterPlaceholder="Filter"
        />
        <Column
          header="Survey period"
          field="surveyFromDateMs"
          filterField="surveyPeriodDisplay"
          body={formatSurveyPeriod}
          sortable
          filter
          filterPlaceholder="Filter"
        />
        <Column
          header="Survey expiry date"
          field="expirationDateMs"
          filterField="expirationDateNew"
          body={formatExpiryDate}
          sortable
          filter
          filterPlaceholder="Filter"
        />
        <Column
          header="Share email reminder"
          body={(rowData) => {
            const expired = isSurveyExpired(rowData.expirationDate)
            if (expired) {
              return (
                <div className="icon-container">
                  <Tooltip title="Survey expired - reminder unavailable" arrow>
                    <img
                      src="./assets/img/formsapps/email-reminder.png"
                      alt="Reminder unavailable"
                      className="actions-icon"
                      style={{ fontSize: "16px", opacity: 0.35, cursor: "not-allowed", pointerEvents: "none" }}
                    />
                  </Tooltip>
                </div>
              )
            }

            return (
              <div className="icon-container">
                <Tooltip title="Share email reminder" arrow>
                  <img
                    src="./assets/img/formsapps/email-reminder.png"
                    alt="Share email reminder"
                    className="actions-icon"
                    style={{ fontSize: "16px" }}
                    onClick={() => sendReminder(rowData)}
                  />
                </Tooltip>
              </div>
            )
          }}
          headerStyle={{ textAlign: "center" }}
        />
        <Column
          header="Response dashboard"
          field="responseDashboard"
          body={(rowData) => (
            <div className="icon-container">
              <Tooltip title="View Responses" arrow>
                <Link
                  to={`/employee-commute?from=${rowData.surveyFromDate}&to=${rowData.surveyToDate}&rowData=${rowData.rowKey}`}
                >
                  <img
                    src="./assets/img/formsapps/response.svg"
                    alt="View Responses"
                    className="actions-icon"
                    style={{
                      fontSize: "16px",
                      margin: "0",
                      marginTop: "-2px",
                    }}
                  />
                </Link>
              </Tooltip>
            </div>
          )}
          headerStyle={{ textAlign: "center" }}
        />
        <Column
          header="Download responses"
          body={(rowData) => (
            <div className="icon-container">
              <Tooltip title="Download Responses" arrow>
                <img
                  src="./assets/img/formsapps/download.svg"
                  alt="Download Responses"
                  className="actions-icon"
                  style={{ fontSize: "20px" }}
                  onClick={() => downloadResponse(rowData)}
                />
              </Tooltip>
            </div>
          )}
          style={{ paddingRight: "30px" }}
          headerStyle={{ textAlign: "center" }}
        />
      </DataTable>
    )
  }

  const renderSiteEmissionsTable = () => {
    return (
      <DataTable
        className="ec-commute-deep-dive-datatable"
        value={siteEmissionsData}
        paginator
        rows={10}
        rowsPerPageOptions={[10, 20, 25, 50]}
        tableStyle={{ minWidth: "72rem" }}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        filters={siteEmissionsFilters}
        filterDisplay="row"
        globalFilterFields={siteEmissionsGlobalFilterFields}
        onFilter={(e) => setSiteEmissionsFilters(e.filters)}
        emptyMessage="No data found."
      >
        <Column header="Reporting period" field="reportingPeriod" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "10rem" }} filterHeaderStyle={{ minWidth: "10rem" }} bodyStyle={{ minWidth: "10rem", whiteSpace: "nowrap" }} />
        <Column header="Site Name" field="siteName" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "13rem" }} filterHeaderStyle={{ minWidth: "13rem" }} bodyStyle={{ minWidth: "13rem", whiteSpace: "nowrap" }} />
        <Column header="Total Employees" field="totalEmployees" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "7.5rem" }} filterHeaderStyle={{ minWidth: "7.5rem" }} bodyStyle={{ minWidth: "7.5rem", whiteSpace: "nowrap" }} />
        <Column header="Respondents" field="respondents" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "7.5rem" }} filterHeaderStyle={{ minWidth: "7.5rem" }} bodyStyle={{ minWidth: "7.5rem", whiteSpace: "nowrap" }} />
        <Column header="Non-Respondents" field="nonRespondents" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "7.5rem" }} filterHeaderStyle={{ minWidth: "7.5rem" }} bodyStyle={{ minWidth: "7.5rem", whiteSpace: "nowrap" }} />
        <Column header="Transport Emissions (tCO₂)" field="transportEmissions" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "9rem" }} filterHeaderStyle={{ minWidth: "9rem" }} bodyStyle={{ minWidth: "9rem", whiteSpace: "nowrap" }} />
        <Column header="WFH Emissions (tCO₂)" field="wfhEmissions" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "9rem" }} filterHeaderStyle={{ minWidth: "9rem" }} bodyStyle={{ minWidth: "9rem", whiteSpace: "nowrap" }} />
        <Column header="Total Emissions (tCO₂)" field="totalEmissions" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "9rem" }} filterHeaderStyle={{ minWidth: "9rem" }} bodyStyle={{ minWidth: "9rem", whiteSpace: "nowrap" }} />
      </DataTable>
    )
  }

  const renderEmployeeEmissionsTable = () => {
    return (
      <DataTable
        className="ec-commute-deep-dive-datatable"
        value={employeeEmissionsData}
        paginator
        rows={10}
        rowsPerPageOptions={[10, 20, 25, 50]}
        tableStyle={{ minWidth: "122rem" }}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        filters={employeeEmissionsFilters}
        filterDisplay="row"
        globalFilterFields={employeeEmissionsGlobalFilterFields}
        onFilter={(e) => setEmployeeEmissionsFilters(e.filters)}
        emptyMessage="No data found."
      >
        <Column header="Employee ID" field="employeeID" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "8.5rem" }} filterHeaderStyle={{ minWidth: "8.5rem" }} bodyStyle={{ minWidth: "8.5rem", whiteSpace: "nowrap" }} />
        <Column header="Name" field="name" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "18rem" }} filterHeaderStyle={{ minWidth: "18rem" }} bodyStyle={{ minWidth: "18rem", whiteSpace: "nowrap" }} />
        <Column header="Site Location" field="siteLocation" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "15rem" }} filterHeaderStyle={{ minWidth: "15rem" }} bodyStyle={{ minWidth: "15rem", whiteSpace: "nowrap" }} />
        <Column header="Reporting period" field="reportingPeriod" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "10.5rem" }} filterHeaderStyle={{ minWidth: "10.5rem" }} bodyStyle={{ minWidth: "10.5rem", whiteSpace: "nowrap" }} />
        <Column header="Office Days" field="officeDays" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "6rem" }} filterHeaderStyle={{ minWidth: "6rem" }} bodyStyle={{ minWidth: "6rem", whiteSpace: "nowrap" }} />
        <Column header="WFH Days" field="wfhDays" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "6rem" }} filterHeaderStyle={{ minWidth: "6rem" }} bodyStyle={{ minWidth: "6rem", whiteSpace: "nowrap" }} />
        <Column header="Primary Mode of Transport" field="primaryModeOfTransport" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "12rem" }} filterHeaderStyle={{ minWidth: "12rem" }} bodyStyle={{ minWidth: "12rem", whiteSpace: "nowrap" }} />
        <Column header="Fuel Type" field="fuelType" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "7.5rem" }} filterHeaderStyle={{ minWidth: "7.5rem" }} bodyStyle={{ minWidth: "7.5rem", whiteSpace: "nowrap" }} />
        <Column header="Transport Type" field="transportType" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "10rem" }} filterHeaderStyle={{ minWidth: "10rem" }} bodyStyle={{ minWidth: "10rem", whiteSpace: "nowrap" }} />
        <Column header="Transport Emissions (kg CO₂e/week)" field="transportEmissionsPerWeek" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "9rem" }} filterHeaderStyle={{ minWidth: "9rem" }} bodyStyle={{ minWidth: "9rem", whiteSpace: "nowrap" }} />
        <Column header="WFH Emissions (kg CO₂e/week)" field="wfhEmissions" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "9rem" }} filterHeaderStyle={{ minWidth: "9rem" }} bodyStyle={{ minWidth: "9rem", whiteSpace: "nowrap" }} />
        <Column header="WFH energy Accuracy" field="wfhEnergyAccuracy" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "10rem" }} filterHeaderStyle={{ minWidth: "10rem" }} bodyStyle={{ minWidth: "10rem", whiteSpace: "nowrap" }} />
        <Column header="Total Emissions (kg CO₂e/week)" field="totalEmissionsPerWeek" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "9rem" }} filterHeaderStyle={{ minWidth: "9rem" }} bodyStyle={{ minWidth: "9rem", whiteSpace: "nowrap" }} />
        <Column header="Total Emissions (kg CO₂e/year)" field="totalEmissionsPerYear" sortable filter filterPlaceholder="Filter" headerStyle={{ minWidth: "9rem" }} filterHeaderStyle={{ minWidth: "9rem" }} bodyStyle={{ minWidth: "9rem", whiteSpace: "nowrap" }} />
      </DataTable>
    )
  }

  return (
    <div id="wrapper">
      <style>{filterFormStyles}</style>
      <div>
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>
      {renderHeader1()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        <div className="container formpd">
          <div className="row mb-4">
            <div className="col-md-8">
              <div className="d-flex flex-column steps__containt-hader">
                <h1 className="title">Employee commute survey</h1>
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                  <Link underline="hover" color="inherit" to="/forms-apps">
                    Forms & Apps
                  </Link>
                  <Typography color="text.primary">Employee commute survey</Typography>
                </Breadcrumbs>
              </div>
            </div>
            <div className="col-md-4 text-md-end">
              <div className="steps__containt-hader">
                <Tooltip title={canStartAssessment ? "" : "You don't have permission to start a survey"} arrow>
                <a className="add-cta" href={canStartAssessment ? "/start-new-survey" : undefined} style={canStartAssessment ? undefined : { opacity: 0.45, cursor: "not-allowed" }}>
                  + Start New Survey
                </a>
                </Tooltip>
              </div>
            </div>
          </div>

          <div className="tabs-container mb-4">
            <div className="main-tabs d-flex">
              <div
                className={`tab-item d-flex flex-column align-items-center ${activeMainTab === "overview" ? "active" : ""}`}
                onClick={() => {
                  setActiveMainTab("overview")
                  listSurvey()
                }}
                style={{
                  padding: "10px 20px",
                  cursor: "pointer",
                  borderBottom: activeMainTab === "overview" ? "2px solid #407775" : "2px solid transparent",
                  color: activeMainTab === "overview" ? "#407775" : "#666",
                  fontWeight: activeMainTab === "overview" ? "600" : "400",
                  marginRight: "20px",
                }}
                onMouseEnter={() => {
                  if (activeMainTab !== "overview") {
                    setHoveredMainTab("overview")
                  }
                }}
                onMouseLeave={() => {
                  setHoveredMainTab(null)
                }}
              >
                <img
                  src={
                    activeMainTab === "overview"
                      ? "./assets/img/formsapps/overview_color.svg"
                      : hoveredMainTab === "overview"
                        ? "./assets/img/formsapps/overview_grey.svg"
                        : "./assets/img/formsapps/overview_grey.svg"
                  }
                  alt="Overview"
                  style={{ width: "24px", height: "24px", marginBottom: "4px" }}
                />
                Overview
              </div>
              <div
                className={`tab-item d-flex flex-column align-items-center ${activeMainTab === "deep-dive" ? "active" : ""}`}
                onClick={() => {
                  setActiveMainTab("deep-dive")
                }}
                style={{
                  padding: "10px 20px",
                  cursor: "pointer",
                  borderBottom: activeMainTab === "deep-dive" ? "2px solid #407775" : "2px solid transparent",
                  color: activeMainTab === "deep-dive" ? "#407775" : "#666",
                  fontWeight: activeMainTab === "deep-dive" ? "600" : "400",
                }}
                onMouseEnter={() => {
                  if (activeMainTab !== "deep-dive") {
                    setHoveredMainTab("deep-dive")
                  }
                }}
                onMouseLeave={() => {
                  setHoveredMainTab(null)
                }}
              >
                <img
                  src={
                    activeMainTab === "deep-dive"
                      ? "./assets/img/formsapps/Deep_Dive_color.svg"
                      : hoveredMainTab === "deep-dive"
                        ? "./assets/img/formsapps/Deep_Dive_grey.svg"
                        : "./assets/img/formsapps/Deep_Dive_grey.svg"
                  }
                  alt="Deep Dive"
                  style={{ width: "24px", height: "24px", marginBottom: "4px" }}
                />
                Deep Dive
              </div>
            </div>
          </div>

          {activeMainTab === "deep-dive" && (
            <div className="ec-commute-subtabs-row mb-3">
              <div className="ec-commute-subtabs-inner">
                <div className="sub-tabs d-flex flex-wrap">
                <div
                  className={`sub-tab-item ${activeSubTab === "employee-level" ? "active" : ""}`}
                  onClick={() => setActiveSubTab("employee-level")}
                  style={{
                    padding: "8px 16px",
                    cursor: "pointer",
                    borderBottom: activeSubTab === "employee-level" ? "2px solid #407775" : "2px solid transparent",
                    color: activeSubTab === "employee-level" ? "#407775" : "#666",
                    fontWeight: activeSubTab === "employee-level" ? "500" : "400",
                    marginRight: "20px",
                    fontSize: "14px",
                  }}
                  onMouseEnter={(e) => {
                    if (activeSubTab !== "employee-level") {
                      e.target.style.color = "#999"
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeSubTab !== "employee-level") {
                      e.target.style.color = "#666"
                    }
                  }}
                >
                  Employee Level Emissions
                </div>
                <div
                  className={`sub-tab-item ${activeSubTab === "site-level" ? "active" : ""}`}
                  onClick={() => setActiveSubTab("site-level")}
                  style={{
                    padding: "8px 16px",
                    cursor: "pointer",
                    borderBottom: activeSubTab === "site-level" ? "2px solid #407775" : "2px solid transparent",
                    color: activeSubTab === "site-level" ? "#407775" : "#666",
                    fontWeight: activeSubTab === "site-level" ? "500" : "400",
                    fontSize: "14px",
                  }}
                  onMouseEnter={(e) => {
                    if (activeSubTab !== "site-level") {
                      e.target.style.color = "#999"
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeSubTab !== "site-level") {
                      e.target.style.color = "#666"
                    }
                  }}
                >
                  Site-Level Emissions
                </div>
                </div>
                <div className="ec-commute-deep-dive-search">
                  <span className="p-input-icon-left">
                    <img src="assets/img/search.png" className="search-i" alt="" />
                    <InputText value={activeSubTab === "employee-level" ? employeeEmissionsGlobalFilterValue : siteEmissionsGlobalFilterValue} onChange={activeSubTab === "employee-level" ? onEmployeeEmissionsGlobalFilterChange : onSiteEmissionsGlobalFilterChange} placeholder="Search" />
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="survey-content">
            <div className="data-responsive">
              {activeMainTab === "overview" && renderOverviewTable()}
              {activeMainTab === "deep-dive" && deepDiveLoading && (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
                  <CircularProgress style={{ color: "#407775" }} />
                </div>
              )}
              {activeMainTab === "deep-dive" && !deepDiveLoading && activeSubTab === "employee-level" && renderEmployeeEmissionsTable()}
              {activeMainTab === "deep-dive" && !deepDiveLoading && activeSubTab === "site-level" && renderSiteEmissionsTable()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmployeeCommuteSurvey
